import { gql } from '@apollo/client';
import { Box, Button, Center, HStack, useDisclosure, useToast } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { isNil } from 'lodash';
import { PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { useAuth } from '~auth/useAuth';
import { DeleteConfigurationModal } from '~components/configurations/DeleteConfigurationModal';
import { useDefaultConfiguration } from '~components/configurations/useDefaultConfiguration';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { EditIcon, TrashIcon } from '~components/ui/icons';
import { ConfigurationStatus } from '~graphql/__generated__/types';
import { EditConfigurationModal } from '../../EditConfigurationModal';
import { ConfigurationTable_ConfigurationFragment } from '../__generated__/ConfigurationsTable.graphql';
import { useCreateDraftMutation } from './__generated__/EditDeleteCell.graphql';

export function EditDeleteCell({
  value,
}: PropsWithChildren<
  CellProps<ConfigurationTable_ConfigurationFragment, ConfigurationTable_ConfigurationFragment>
>) {
  const toast = useToast();
  const { verifyUserPermissions } = useAuth();
  const deleteConfigurationModal = useDisclosure();
  const [createDraft, { loading }] = useCreateDraftMutation();
  const { t } = useTranslation();
  const hasConfigurationUpdatePermission = verifyUserPermissions([Permission.ConfigurationUpdate]);
  const hasConfigurationDeletePermission = verifyUserPermissions([Permission.ConfigurationDelete]);
  const { defaultConfigurations, multiClaimDefaultConfigurations } = useDefaultConfiguration();
  const editConfigurationModal = useDisclosure();
  const defaultConfigurationSet = useMemo(() => {
    const result = new Set<string>();

    if (!isNil(defaultConfigurations) && defaultConfigurations?.length > 0) {
      defaultConfigurations.forEach((defConfig) => {
        defConfig.defaultConfigurations?.forEach((config) => {
          result.add(config.configurationId);
        });
      });
    }

    if (!isNil(multiClaimDefaultConfigurations) && multiClaimDefaultConfigurations?.length > 0) {
      multiClaimDefaultConfigurations.forEach((defConfig) => {
        defConfig.defaultConfigurations?.forEach((config) => {
          result.add(config.configurationId);
        });
      });
    }

    return result;
  }, [defaultConfigurations, multiClaimDefaultConfigurations]);

  const isConfigurationSetAsDefault = (configurationId: string) => {
    return defaultConfigurationSet.has(configurationId);
  };

  const navigate = useNavigate();

  const onEditClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (value.state === ConfigurationStatus.Published && !value.draft) {
      editConfigurationModal.onOpen();
    } else if (value.state === ConfigurationStatus.Published && value.draft) {
      navigate(`${value.id}/config-summary`);
    } else {
      navigate(`${value.id}/settings`);
    }
  };

  const handleEdit = async () => {
    try {
      if (value.state === ConfigurationStatus.Published && !value.draft) {
        await createDraft({
          variables: {
            configurationId: value.id,
          },
          refetchQueries: ['CustomerConfigDetailPage'],
          awaitRefetchQueries: true,
        });
      }
      navigate(`${value.id}/settings`, {
        state: {
          editConfig: true,
        },
      });
    } catch (err) {
      toast({
        status: 'error',
        title: t('errorCreatingDraft'),
        description: t('errorCreatingDraftDesc'),
      });
    }
  };
  return (
    <>
      <Box
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        fontWeight="semibold"
        width={'100%'}
      >
        <Center>
          <HStack>
            <Button
              variant={'ghost'}
              p={0}
              color="blue.500"
              onClick={onEditClick}
              isDisabled={!hasConfigurationUpdatePermission}
            >
              {loading ? <FormLabelPendingIndicator isPending /> : <EditIcon boxSize={6} m={0} />}
            </Button>
            <Button
              variant={'ghost'}
              p={0}
              color="blue.500"
              onClick={(e) => {
                deleteConfigurationModal.onOpen();
                e.stopPropagation();
              }}
              isDisabled={!hasConfigurationDeletePermission}
            >
              <TrashIcon boxSize={6} m={0} />
            </Button>
          </HStack>
        </Center>
      </Box>
      <DeleteConfigurationModal
        configurationId={value.id}
        isConfigurationSetAsDefault={isConfigurationSetAsDefault(value.id)}
        onCancel={deleteConfigurationModal.onClose}
        isOpen={deleteConfigurationModal.isOpen}
      />
      <EditConfigurationModal
        editHandler={handleEdit}
        isOpen={editConfigurationModal.isOpen}
        onCancel={editConfigurationModal.onClose}
      />
    </>
  );
}

EditDeleteCell.graphql = {
  mutations: {
    CreateDraft: gql`
      mutation CreateDraft($configurationId: String!) {
        createDraftConfiguration(configurationId: $configurationId) {
          id
        }
      }
    `,
  },
};
